// Live pitch detection for singing practice.
//
// SAFEGUARDING CONTRACT (do not weaken): the microphone is analysed in real
// time ONLY. No MediaRecorder, no buffers are stored, nothing is played back,
// nothing is uploaded or leaves the device. Microphone tracks are stopped the
// instant listening ends. The child hears themselves only through the air,
// exactly as in a rehearsal room.
import { useEffect, useRef, useState } from "react";

let stream = null;
let actx = null;
let analyser = null;
let buf = null;
let rafId = null;

const NOTE_NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];

export function midiName(m) {
  return NOTE_NAMES[((Math.round(m) % 12) + 12) % 12] + (Math.floor(Math.round(m) / 12) - 1);
}

// Autocorrelation pitch tracker — voice-friendly range 80–1200 Hz.
// Uses the classic "first strong peak" rule to avoid octave-down errors:
// the first local maximum of the normalised ACF above threshold is the
// fundamental, never a later subharmonic peak.
function detectPitch(b, sr) {
  const N = b.length;
  let r0 = 0;
  for (let i = 0; i < N; i++) r0 += b[i] * b[i];
  const rms = Math.sqrt(r0 / N);
  if (rms < 0.01 || r0 === 0) return null; // silence / too quiet
  const lagMin = Math.floor(sr / 1200);
  const lagMax = Math.min(Math.ceil(sr / 80), N - 2);
  const rn = new Float32Array(lagMax + 2);
  for (let lag = lagMin; lag <= lagMax; lag++) {
    let s = 0;
    for (let i = 0; i < N - lag; i++) s += b[i] * b[i + lag];
    rn[lag] = s / r0; // normalised, ~1.0 at the true period
  }
  let bestLag = -1;
  for (let lag = lagMin + 1; lag < lagMax; lag++) {
    if (rn[lag] >= 0.6 && rn[lag] > rn[lag - 1] && rn[lag] >= rn[lag + 1]) {
      bestLag = lag; // first strong peak = fundamental
      break;
    }
  }
  if (bestLag < 0) { // fallback for noisy/quiet signals: global maximum
    let best = 0;
    for (let lag = lagMin; lag <= lagMax; lag++) {
      if (rn[lag] > best) { best = rn[lag]; bestLag = lag; }
    }
    if (best < 0.45) return null;
  }
  // parabolic interpolation for cents-level accuracy
  const shift = (rn[bestLag - 1] - rn[bestLag + 1]) / (2 * (rn[bestLag - 1] - 2 * rn[bestLag] + rn[bestLag + 1]) || 1);
  const freq = sr / (bestLag + (Number.isFinite(shift) ? shift : 0));
  const midi = 69 + 12 * Math.log2(freq / 440);
  return { freq, midi, cents: Math.round((midi - Math.round(midi)) * 100), name: midiName(midi), clarity: rn[bestLag] };
}

export async function startPitchListener(onPitch) {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("no-mic-api");
  // iPhone Safari hardening: retry with bare constraints if the detailed ones
  // are rejected (OverconstrainedError/TypeError), but never retry a denial.
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
    });
  } catch (e) {
    if (e?.name === "NotAllowedError" || e?.name === "SecurityError") throw e;
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  }
  const AC = window.AudioContext || window.webkitAudioContext;
  actx = new AC();
  if (actx.state === "suspended") await actx.resume(); // iOS: inside the tap handler
  const src = actx.createMediaStreamSource(stream);
  analyser = actx.createAnalyser();
  analyser.fftSize = 2048;
  src.connect(analyser);
  buf = new Float32Array(analyser.fftSize);
  // Safari < 14.1 (older iPhones) has no getFloatTimeDomainData — fall back to bytes.
  const useFloat = typeof analyser.getFloatTimeDomainData === "function";
  const byteBuf = useFloat ? null : new Uint8Array(analyser.fftSize);
  const loop = () => {
    if (!analyser) return;
    if (useFloat) {
      analyser.getFloatTimeDomainData(buf);
    } else {
      analyser.getByteTimeDomainData(byteBuf);
      for (let i = 0; i < buf.length; i++) buf[i] = (byteBuf[i] - 128) / 128;
    }
    onPitch(detectPitch(buf, actx ? actx.sampleRate : 44100));
    rafId = requestAnimationFrame(loop);
  };
  loop();
}

export function stopPitchListener() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
  if (stream) stream.getTracks().forEach((t) => t.stop());
  stream = null;
  if (actx) actx.close().catch(() => { /* already closed */ });
  actx = null;
  analyser = null;
  buf = null;
}

// React hook wrapper: start/stop the listener, get live pitch frames.
export function usePitch() {
  const [pitch, setPitch] = useState(null);
  const [error, setError] = useState("");
  const [listening, setListening] = useState(false);
  const activeRef = useRef(false);
  const start = async () => {
    setError("");
    try {
      await startPitchListener(setPitch);
      activeRef.current = true;
      setListening(true);
    } catch (e) {
      const n = e?.name || "";
      setError(n === "NotAllowedError" || n === "SecurityError" ? "denied"
        : n === "NotReadableError" ? "busy"
        : n === "NotFoundError" ? "unavailable"
        : e?.message === "no-mic-api" ? "unavailable" : "unavailable");
    }
  };
  const stop = () => {
    activeRef.current = false;
    stopPitchListener();
    setListening(false);
    setPitch(null);
  };
  useEffect(() => () => stopPitchListener(), []);
  return { pitch, error, start, stop, listening };
}

// Shared child-friendly microphone gate panel (safeguarding wording is part of
// the product promise — do not change lightly).
export const MIC_NOTE = "ChoirQuest only listens to check your pitch — it never records, saves or plays back your voice.";
